import { getArea } from 'ol/sphere';
import GeoJSON from 'ol/format/GeoJSON';
import { Geometry } from '@ais-ui/api-spec/shapes';

export interface AreaResult {
  /** Area value in km² */
  area: number;
  /** Unit string (always 'km²') */
  areaUnit: string;
  /**
   * Locale-independent formatted string like "123.45 km²". Safe to persist
   * (e.g. shape properties) since it doesn't vary by the user's locale.
   */
  formatted: string;
  /**
   * Locale-aware formatted string (e.g. "12,345.67 km²" or "12.345,67 km²")
   * for UI display only — must not be persisted, since it's not guaranteed
   * to round-trip or compare consistently across locales.
   */
  displayFormatted: string;
}

/**
 * Calculate the area of a polygon geometry.
 * Uses spherical calculations for accurate results.
 *
 * @param geometry - GeoJSON geometry object (must be Polygon type)
 * @returns AreaResult or null if geometry is not a Polygon
 */
export const calculatePolygonArea = (geometry: Geometry): AreaResult | null => {
  if (geometry.type !== 'Polygon') {
    return null;
  }

  const format = new GeoJSON();
  const olGeometry = format.readGeometry(geometry);
  const areaInSquareMeters = getArea(olGeometry, { projection: 'EPSG:4326' });

  // Convert to km² and round to 2 decimal places
  const area = Math.round((areaInSquareMeters / 1_000_000) * 100) / 100;

  return {
    area,
    areaUnit: 'km²',
    formatted: `${area} km²`,
    displayFormatted: `${area.toLocaleString()} km²`,
  };
};

/**
 * Calculate the area of a circle given its radius.
 * Uses the formula: A = π × r²
 *
 * @param radiusKm - Radius in kilometers
 * @returns AreaResult
 */
export const calculateCircleArea = (radiusKm: number): AreaResult => {
  // Calculate area using π × r² and round to 2 decimal places
  const area = Math.round(Math.PI * Math.pow(radiusKm, 2) * 100) / 100;

  return {
    area,
    areaUnit: 'km²',
    formatted: `${area} km²`,
    displayFormatted: `${area.toLocaleString()} km²`,
  };
};

/**
 * Calculate area for a shape based on its geometry type.
 * Supports Polygon and Circle (Point with radius) geometries.
 *
 * @param geometry - GeoJSON geometry object
 * @param radius - Optional radius in km (for circle shapes)
 * @returns AreaResult or null if geometry type doesn't support area calculation
 */
export const calculateShapeArea = (
  geometry: Geometry | undefined,
  radius?: number,
): AreaResult | null => {
  if (!geometry) {
    return null;
  }

  // Circle: Point geometry with radius
  if (geometry.type === 'Point' && radius !== undefined) {
    return calculateCircleArea(radius);
  }

  // Polygon
  if (geometry.type === 'Polygon') {
    return calculatePolygonArea(geometry);
  }

  // LineString, Point (without radius), etc. don't have area
  return null;
};

/**
 * Converts a persisted, locale-independent area string (as produced by
 * `AreaResult.formatted`, e.g. "12669.11 km²") into a locale-aware display
 * string (e.g. "12,669.11 km²"), for showing previously-saved area values
 * consistently with newly-calculated ones in the UI.
 *
 * Falls back to returning the input unchanged if it doesn't match the
 * expected "<number> <unit>" shape (e.g. legacy or unexpected data).
 *
 * @param persistedFormatted - a value from `AreaResult.formatted`
 */
export const formatPersistedAreaForDisplay = (
  persistedFormatted: string,
): string => {
  const match = /^(-?\d+(?:\.\d+)?) (.+)$/.exec(persistedFormatted);
  if (!match) {
    return persistedFormatted;
  }

  const [, numberPart, unit] = match;
  const parsed = Number(numberPart);
  return Number.isFinite(parsed)
    ? `${parsed.toLocaleString()} ${unit}`
    : persistedFormatted;
};
